/**
 * Phase 45 Plan 45-06 — task status pill.
 *
 * Petra UAT 2026-05-19 — pill now prefers the live `task_states.color` hex
 * supplied per-task by the backend (TaskRowDto.taskStateColor). The kanban
 * column header reads the same source on the project board, so My Tasks
 * pills and board columns share a palette. Falls back to the canonical
 * STATUS_MAP (UI-SPEC §Surface 2 Status pill) for tasks without a colour
 * — legacy rows with no task_state row, or unknown DB statuses.
 *
 * UI-SPEC §A11y Contract — dot is colour-only so the label is announced
 * explicitly via aria-label="Status: {label}".
 */
import type { CSSProperties } from 'react';

export type TaskStatus = 'Triage' | 'Open' | 'In progress' | 'Blocked' | 'Done' | string;

interface StatusCfg {
  label: string;
  bg: string;
  fg: string;
  dot: string;
}

export const STATUS_MAP: Record<string, StatusCfg> = {
  Triage: { label: 'Triage', bg: '#FEE2E2', fg: '#991B1B', dot: '#EF4444' },
  'In progress': { label: 'In progress', bg: '#FFF1E5', fg: '#9A3F0B', dot: '#FC8332' },
  Open: { label: 'Todo', bg: '#F4F1EC', fg: '#5C5C68', dot: '#8E8E98' },
  Done: { label: 'Done', bg: '#E8F5E9', fg: '#1B5E20', dot: '#10B981' },
  Blocked: { label: 'Blocked', bg: '#F3E8FF', fg: '#6B21A8', dot: '#A855F7' },
};

const FALLBACK_CFG: StatusCfg = {
  label: '—',
  bg: '#F4F1EC',
  fg: '#5C5C68',
  dot: '#8E8E98',
};

export interface MtStatusPillProps {
  status: TaskStatus | null | undefined;
  /**
   * Live colour from task_states.color (DB). When present overrides the
   * STATUS_MAP look-up so each project's customisable state palette flows
   * to the pill verbatim. ~13% alpha for the bg, full hex for dot, full
   * hex for fg text (most state colours are dark enough to read on a
   * lightened-of-themselves background; for very light hues the contrast
   * still passes WCAG-AA on the small pill label).
   */
  color?: string | null;
  className?: string;
}

export function MtStatusPill({ status, color, className = '' }: MtStatusPillProps) {
  // Live colour from DB wins over the hardcoded map. Falls through to map
  // if no colour was supplied; falls through to neutral grey if no match.
  if (color && status) {
    const wrapperStyle: CSSProperties = {
      background: `${color}22`,
      color: color,
    };
    const dotStyle: CSSProperties = { background: color };
    return (
      <span
        aria-label={`Status: ${status}`}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${className}`}
        style={wrapperStyle}
        data-testid="mt-status-pill"
      >
        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={dotStyle} />
        {status}
      </span>
    );
  }

  const known = status ? STATUS_MAP[status] : undefined;
  const cfg: StatusCfg = known ?? { ...FALLBACK_CFG, label: status ?? '—' };
  const wrapperStyle: CSSProperties = { background: cfg.bg, color: cfg.fg };
  const dotStyle: CSSProperties = { background: cfg.dot };
  return (
    <span
      aria-label={`Status: ${cfg.label}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${className}`}
      style={wrapperStyle}
      data-testid="mt-status-pill"
    >
      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={dotStyle} />
      {cfg.label}
    </span>
  );
}
